import React, { type ChangeEvent, useCallback, useMemo } from 'react';
import { cn } from '../../utils/cn';
import type { CartCounterButtonProps } from './cart-counter-button.types';
import { IconButton } from '../icon-button';
import { Icon } from '../../atoms/icon';
import { Minus, Plus } from 'lucide-react';
import { TextInput } from '../text-input';
import { CartCounterVariantEnum } from './cart-counter-button.enum';
import styles from './cart-counter-button.module.css';

export const CartCounterButton: React.FC<CartCounterButtonProps> = ({
    item,
    handleIncrease,
    handleDecrease,
    isLoading,
    isDisableDecrease,
    isDisableIncreaseProductDetail,
    disabledInput,
    disabled,
    emitQuantityChange,
    containerStyle,
    cartItemReference,
    variant = CartCounterVariantEnum.DEFAULT,
}) => {
    const currentQty = cartItemReference?.qty;

    const isDecreaseDisabled = isDisableDecrease || currentQty === 1;

    const onDecreaseButtonPressed = useCallback(() => {
        if (disabled) {
            return;
        }
        handleDecrease({ item: item, value: 1, cartItemReference, parentProduct: cartItemReference?.parentProduct });
    }, [cartItemReference, handleDecrease, item, disabled]);

    const onIncreaseButtonPressed = useCallback(() => {
        if (disabled) {
            return;
        }
        handleIncrease({
            item: item,
            value: 1,
            cartItemReference,
            parentProduct: cartItemReference?.parentProduct,
        });
    }, [cartItemReference, handleIncrease, item, disabled]);

    const onQuantityChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            const quantity = event?.target?.value;
            let quantityChanged = 1; // default will be 1
            if (typeof quantity === 'number') {
                // check if quantity is a number then parse it
                quantityChanged = Number.parseInt(quantity, 10);
            }
            if (!Number.isNaN(quantityChanged)) {
                emitQuantityChange({ item: item, value: quantityChanged, cartItemReference });
            }
        },
        [cartItemReference, emitQuantityChange, item]
    );
    const containerClass = useMemo(
        () =>
            cn(
                variant === CartCounterVariantEnum.COMPACT ? styles.compact : styles.default,
                styles.container,
                containerStyle,
                isLoading && styles.isLoading
            ),
        [variant, containerStyle, isLoading]
    );

    const counterClass = useMemo(
        () =>
            cn(
                variant === CartCounterVariantEnum.COMPACT ? styles.compact : styles.default,
                styles.couterContainer,
                disabled && styles.disabled
            ),
        [disabled, variant]
    );

    return (
        <div className={containerClass}>
            <div className={counterClass}>
                <IconButton
                    className={styles.decreaseButton}
                    icon={<Icon icon={Minus} size={12} containerClassName={styles.icon} />}
                    disabled={isDecreaseDisabled}
                    onClick={onDecreaseButtonPressed}
                />

                <div className={styles.primaryContent}>
                    <TextInput
                        className={styles.inputContainer}
                        inputClassName={styles.input}
                        value={String(currentQty).padStart(2, '0')}
                        onChange={onQuantityChange}
                        disabled={disabledInput}
                    />
                </div>

                <IconButton
                    className={styles.increaseButton}
                    icon={<Icon icon={Plus} size={12} containerClassName={styles.icon} />}
                    disabled={isDisableIncreaseProductDetail}
                    onClick={onIncreaseButtonPressed}
                />
            </div>
        </div>
    );
};
